import React, { useEffect, useState, useRef } from 'react';
import axios from "../lib/axios";
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { API_BASE_URL } from '../config';
import { useJourney } from "@/contexts/JourneyContext";
import JourneyHeader from "@/components/JourneyHeader";

const RETRY_INTERVAL_MS = 0.5 * 60 * 1000; // 5 minutes

const WaitingForUpdates = ({ isBuilderMode = false }: { isBuilderMode?: boolean }) => {
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(RETRY_INTERVAL_MS / 1000);
  const navigate = useNavigate();
  const { refresh: refreshJourney } = useJourney();

  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  const checkVerificationStatus = () => {
    setLoading(true);

    axios
      .post(`${API_BASE_URL}/api/verification_wrapper_for_update_and_check/`)
      .then((res) => {
        if (res.status !== 200) return;

        refreshJourney()
          .finally(() => {
            setTimeout(() => {
              if (res.data.status_msg === "failed") {
                navigate('/final-reject-state');
              } else if (res.data.status_msg === "pending") {
                // do nothing → retry handled by timer
              } else {
                // Use next_page_url from backend (follows LenderBAPJourney template)
                // e.g. /doc-push, /esign, /loan-success depending on journey config
                const nextUrl = res.data.next_page_url;
                navigate(nextUrl || '/doc-push');
              }
            }, 150);
          });
      })
      .catch((err) => {
        if (confirm(`Error saving data: ${err.message}.\nPress OK to reload the page.`)) {
          window.location.reload();
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isBuilderMode) return;

    checkVerificationStatus();

    timerRef.current = setInterval(() => {
      checkVerificationStatus();
      setCountdown(RETRY_INTERVAL_MS / 1000);
    }, RETRY_INTERVAL_MS);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : prev));
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [isBuilderMode]);

  const handleExitToHome = () => {
    navigate('/home');
  };

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">⏳</div>
        <p className="font-semibold text-gray-500">Verification Pending</p>
        <p className="text-xs mt-1">Polls backend every 30s and advances journey when verification completes.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="bg-white shadow-md rounded-lg p-6 mb-6 text-center">
          <h2 className="text-2xl font-bold text-intellend-green mb-6">
            Waiting For Updates
          </h2>

          {loading && (
            <p className="text-sm text-gray-500 mb-4">
              Checking status...
            </p>
          )}

          <p className="text-lg text-gray-700 mb-4">
            Your application is currently on hold.
          </p>

          <p className="text-sm text-gray-500">
            Next check in {minutes}:{seconds.toString().padStart(2, '0')}
          </p>

          <div className="mt-8">
            <Button
              onClick={handleExitToHome}
              className="bg-intellend-green hover:bg-intellend-green/90 w-full md:w-auto"
            >
              Exit to Home
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WaitingForUpdates;
